import React from 'react'
import AgTechTourHelpButton from '@agtech/web/components/App/Tour/AgTechTourHelpButton';
import { AddPlanningTaskDialogTourConfig } from './AddPlanningTaskDialogTourConfig';
import dayjs from 'dayjs';
import { useAgTechQuery, queryResult } from '@agtech/core/data/queries/AgTechDataQueries';
import { AgTechButtonProps, AgTechButton, AgTechClickEventHandler } from '@agtech/web/components/Buttons/AgTechButtons';
import { AgTechFormTextControl } from '@agtech/web/components/Forms/Controls/AgTechFormTextControl';
import { AgTechFormTextAreaControl } from '@agtech/web/components/Forms/Controls/AgTechFormTextAreaControl';
import { AgTechFormNumberControl } from '@agtech/web/components/Forms/Controls/AgTechFormNumberControl';
import { AgTechFormDateControl } from '@agtech/web/components/Forms/Controls/AgTechFormDateControl';
import AgTechFormLookup from '@agtech/web/components/Forms/Controls/Lookups/AgTechFormLookup';
import AgTechTitleDetailHeader from '@agtech/web/components/Headers/AgTechTitleDetailHeader';
import AgTechFormBody from '@agtech/web/components/Forms/Layout/AgTechFormBody';
import { AgTechFormRow } from '@agtech/web/components/Forms/Layout/AgTechFormRow';
import AgTechFormDialog from '@agtech/web/components/Portals/Dialogs/AgTechFormDialog';
import { DefaultCreatePlanningTaskDTO } from '@sales/data/planningTasks/PlanningTaskData';
import { usePlanningTaskCreateAction } from '@sales/data/planningTasks/PlanningTaskDataActions';
import { OperationDataGetUsersQuery } from '@sales/data/operation/users/OperationUserQueries';

export type AddPlanningTaskDialogProps = {
    saleId: number
}

const AddPlanningTaskDialog = (props: AddPlanningTaskDialogProps) => {
    const createAction = usePlanningTaskCreateAction();

    const usersQuery = useAgTechQuery({
        query: OperationDataGetUsersQuery,
        projection: users => Object.values(users)
    });

    return (
        <AgTechFormDialog
            width={550}
            structure={() => ({
                header: {
                    header: 'Add Planning Task',
                    settings: {
                        content: {
                            body: () => <AgTechTourHelpButton tourConfig={AddPlanningTaskDialogTourConfig} />
                        }
                    }
                },
                submitButtonText: 'Add Task',
            })}
            dialogData={{
                query: queryResult(DefaultCreatePlanningTaskDTO(props.saleId)),
                getDialogData: d => d
            }}
            dialogAction={{
                action: createAction,
                getActionData: d => d
            }}
        >
            {(formContext) => (
                <AgTechFormBody>
                    <div id='add-task-name'>
                    <AgTechFormRow>
                        <AgTechFormTextControl
                            entityType={formContext.entity}
                            caption='Task Name'
                            value={item => item.taskName}
                            focused={true}
                            required={true}
                            width={100}
                            onControlValueChanged={async (e, val) => ({
                                ...e,
                                taskName: val
                            })}
                        />
                    </AgTechFormRow>
                    </div>
                    <div id='add-task-description'>
                    <AgTechFormRow>
                        <AgTechFormTextAreaControl
                            entityType={formContext.entity}
                            caption='Description'
                            value={item => item.taskDescription}
                            rows={3}
                            width={100}
                            onControlValueChanged={async (e, val) => ({
                                ...e,
                                taskDescription: val
                            })}
                        />
                    </AgTechFormRow>
                    </div>
                    <div id='add-task-dates'>
                    <AgTechFormRow>
                        <AgTechFormDateControl
                            entityType={formContext.entity}
                            caption='Start Date'
                            value={item => dayjs(item.startDate).format('MM-DD-YYYY')}
                            width={50}
                            onControlValueChanged={async (e, val) => ({
                                ...e,
                                startDate: dayjs(val).format('YYYY-MM-DD')
                            })}
                        />
                        <AgTechFormDateControl
                            entityType={formContext.entity}
                            caption='End Date'
                            value={item => dayjs(item.endDate).format('MM-DD-YYYY')}
                            width={50}
                            onControlValueChanged={async (e, val) => ({
                                ...e,
                                endDate: dayjs(val).format('YYYY-MM-DD')
                            })}
                        />
                    </AgTechFormRow>
                    </div>
                    <div id='add-task-priority'>
                    <AgTechFormRow>
                        <AgTechFormNumberControl
                            entityType={formContext.entity}
                            caption='Priority'
                            value={item => item.priority}
                            width={33}
                            onControlValueChanged={async (e, val) => ({
                                ...e,
                                priority: val
                            })}
                        />
                        <AgTechFormNumberControl
                            entityType={formContext.entity}
                            caption='Days Between Notifications'
                            value={item => item.daysBetweenNotifications}
                            width={33}
                            onControlValueChanged={async (e, val) => ({
                                ...e,
                                daysBetweenNotifications: val
                            })}
                        />
                    </AgTechFormRow>
                    </div>
                    <div id='add-task-assignee'>
                    <AgTechFormRow>
                        <AgTechFormLookup
                            entityType={formContext.entity}
                            control={{
                                caption: 'Assigned To',
                                width: 100
                            }}
                            selection={{
                                selectionControlTitle: 'Users',
                                data: {
                                    itemQuery: usersQuery,
                                    selectedItemId: formContext.entity.assignedToUserId?.toString(),
                                    searchItems: (item, filter) => `${item.firstName} ${item.lastName}`.toLowerCase().includes(filter.toLowerCase()),
                                },
                                items: {
                                    itemText: u => `${u.firstName} ${u.lastName}`,
                                    itemHeight: 60,
                                    itemRendering: u => (
                                        <AgTechTitleDetailHeader
                                            title={`${u.firstName} ${u.lastName}`}
                                            detail={u.email}
                                            titleFontSize='font-size-small'
                                            detailFontSize='font-size-very-small'
                                            detailClasses='mt-1-half'
                                        />
                                    )
                                },
                                behavior: {
                                    onItemSelected: async (e, selectedUser) => {
                                        return {
                                            ...formContext.entity,
                                            assignedToUserId: selectedUser?.id ?? null
                                        };
                                    },
                                },
                                popup: {
                                    height: 240,
                                    width: 360
                                }
                            }}
                        />
                    </AgTechFormRow>
                    </div>
                </AgTechFormBody>
            )}
        </AgTechFormDialog>
    )
}

AddPlanningTaskDialog.Button = (props: AddPlanningTaskDialogProps & AgTechButtonProps) => (
    <AgTechButton
        text='Add Task'
        icon='fas fa-plus'
        {...props}
        action={e => AddPlanningTaskDialog.Open(e, props)}
    />
);

AddPlanningTaskDialog.Open = (e: AgTechClickEventHandler, props: AddPlanningTaskDialogProps) => e.dialogs.openDialog({
    dialog: () => <AddPlanningTaskDialog saleId={props.saleId} />
});

export default AddPlanningTaskDialog;
